// src/pages/signin/EmailVerificationPage.js
import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { DATA_SERVICE_BASE_URL } from '../../services/ApiService';
import './EmailVerificationPage.css';

function EmailVerificationPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const searchToken = useMemo(() => new URLSearchParams(location.search).get('token') || '', [location.search]);
  const pathToken = useMemo(() => {
    const parts = location.pathname.split('/');
    const idx = parts.indexOf('verify-email');
    if (idx >= 0 && parts.length > idx + 1) return parts[idx + 1];
    return '';
  }, [location.pathname]);
  const token = searchToken || pathToken;

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState('');

  const rules = {
    length: newPassword.length >= 8,
    uppercase: /[A-Z]/.test(newPassword),
    lowercase: /[a-z]/.test(newPassword),
    digit: /[0-9]/.test(newPassword),
    special: /[!@#$%^&*()]/.test(newPassword),
    match: newPassword.length > 0 && newPassword === confirmPassword,
  };
  const allValid = Object.values(rules).every(Boolean);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setServerError('');

    if (!token) {
      setServerError('Missing or invalid verification token.');
      return;
    }
    if (!allValid) {
      setServerError('Please meet all password requirements before submitting.');
      return;
    }

    setSubmitting(true);
    try {
      const url = `${DATA_SERVICE_BASE_URL}/api/users/reset-password?token=${encodeURIComponent(token)}&newPassword=${encodeURIComponent(newPassword)}`;
      const res = await fetch(url, { method: 'POST', credentials: 'include' });
      if (res.ok) {
        navigate('/login', { replace: true });
      } else {
        let message = 'Failed to verify email. Please try again.';
        const ct = res.headers.get('content-type') || '';
        try {
          if (ct.includes('application/json')) {
            const data = await res.json();
            message = data?.message || data?.error || message;
          } else {
            const text = await res.text();
            message = text || message;
          }
        } catch(_){}
        setServerError(message);
      }
    } catch (err) {
      setServerError(err?.message || 'Unexpected error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="ev-page" style={{ backgroundImage: "url('/landing_page_hero/campus.png')" }}>
      <div className="ev-overlay" />
      <div className="ev-card">
        {/* Left: Form */}
        <div className="ev-left">
          <h2>Verify your email &amp; set your password</h2>
          <p className="ev-intro">
            You're one step away from activating your DocuHounds account.
            Set a secure password and finish getting your study system ready.
          </p>

          {!token && <p className="ev-message">Missing verification token. Check your link or request a new one.</p>}

          {serverError && (
            <div className="ev-error-box" role="alert" aria-live="assertive">
              <div className="ev-error-title">We couldn't verify your email</div>
              <div className="ev-error-details">{serverError}</div>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <input
              type="password"
              placeholder="New password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              disabled={submitting}
              required
              aria-describedby={newPassword ? 'ev-password-rules' : undefined}
            />
            <input
              type="password"
              placeholder="Confirm password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={submitting}
              required
            />

            {newPassword && (
              <div id="ev-password-rules" className="ev-password-rules" aria-live="polite">
                <ul>
                  <li className={rules.length ? 'passed' : 'failed'}>{rules.length ? 'âœ“' : 'âœ—'} At least 8 characters</li>
                  <li className={rules.uppercase ? 'passed' : 'failed'}>{rules.uppercase ? 'âœ“' : 'âœ—'} One uppercase letter</li>
                  <li className={rules.lowercase ? 'passed' : 'failed'}>{rules.lowercase ? 'âœ“' : 'âœ—'} One lowercase letter</li>
                  <li className={rules.digit ? 'passed' : 'failed'}>{rules.digit ? 'âœ“' : 'âœ—'} One digit (0â€“9)</li>
                  <li className={rules.special ? 'passed' : 'failed'}>{rules.special ? 'âœ“' : 'âœ—'} One special character (!@#$%^&amp;*())</li>
                  <li className={rules.match ? 'passed' : 'failed'}>{rules.match ? 'âœ“' : 'âœ—'} Passwords match</li>
                </ul>
              </div>
            )}

            <button type="submit" className="ev-submit-btn" disabled={submitting || !token || !allValid}>
              {submitting ? 'Verifyingâ€¦' : 'Activate Account'}
            </button>
          </form>
          <div className="ev-help">
            <p>Link expired? <a href="/forgot-password">Request a new verification email</a>.</p>
          </div>
        </div>

        {/* Right: Branded panel */}
        <div className="ev-right">
          <img
            src="/scout_images/scout_thumbs_up.png"
            alt="Scout giving thumbs up"
            className="ev-scout-img"
          />
          <h3 className="ev-right-headline">Final step. Then you're in.</h3>
          <p className="ev-right-copy">
            Your courses, deadlines, study tools, and documents are waiting.
            Activate your account and get your semester under control.
          </p>
          <ul className="ev-right-bullets">
            <li>One system for school</li>
            <li>Built for real student life</li>
            <li>Ready when you are</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default EmailVerificationPage;
